import { Button, cn } from '@atlas/ui-primitives'
import { Download, SquareDashedMousePointer, X } from 'lucide-react'
import type { AssetListBulkAction } from './asset-list-card.types'

export interface AssetListSelectionBarProps {
	/** Whether the card is selecting: the tiles are toggles, and this row offers the actions. */
	isSelecting: boolean
	selectionCount: number
	isDownloadPending: boolean
	onStartSelecting: () => void
	/** Leave selection mode and drop the selection - the two are one action, never two. */
	onExitSelecting: () => void
	onDownloadSelected: () => void
	/**
	 * A host's own reason the action cannot run - a selection larger than one request may
	 * carry, say. Never a stand-in for an empty selection, which the row already refuses.
	 */
	isDisabled?: boolean
	/**
	 * How tall the row's controls stand, so a host can line them up with the buttons they sit
	 * beside. Defaults to the compact size a card's heading row has room for; `md` matches the
	 * Button primitive's own default, for a host that puts this row in a page header.
	 */
	size?: 'sm' | 'md'
	/** The host's own words for the action. Absent, the row reads as a download, as it always has. */
	action?: AssetListBulkAction
}

/** The card's own wording: what a bulk action is, absent a host that calls it something else. */
const downloadLabel = (count: number) => (count ? `Download ${count} ${count === 1 ? 'file' : 'files'}` : 'Download')

const CONTROL =
	'inline-flex cursor-pointer items-center gap-1.5 rounded-ds-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50'

/** Both sizes are the Button primitive's, so a host's row of actions stays one row of one height. */
const CONTROL_SIZE = {
	sm: 'h-8 px-2.5 text-ds-sm leading-5',
	md: 'h-10 px-4 text-ds-base',
}

/** The exit control is square, so its width tracks the height rather than the padding. */
const EXIT_SIZE = { sm: 'w-8', md: 'w-10' }

/**
 * The list's action group, sitting in the card's heading row: the way into selecting, and -
 * once selecting - what can be done with the selection.
 *
 * It lives in the heading rather than above the grid so the assets start immediately under the
 * title, and so the actions stay in the one corner every card puts its actions in.
 *
 * The way in is the whole discoverability of the feature, so it is a plain visible control
 * rather than something a hover reveals - and where selecting leads somewhere its own name
 * should state, the host names it ({@link AssetListBulkAction.enterLabel}): at rest that
 * button is all a reader sees of the feature. The download names the number of files it
 * will fetch, because it is N separate browser downloads rather than one archive, and a
 * reader deserves to know that before clicking. The count lives on that action rather than in
 * a label beside it, so the row states it once. Leaving the mode is icon-only: it is the
 * lesser of the two actions, and a header has no room for a second full-width label.
 *
 * The action's WORDS are likewise the host's where it supplies them ({@link AssetListSelectionBarProps.action}),
 * because a host whose action is not a download - composing a package, say - would otherwise
 * have to label it as one, and the label is what tells the reader what the button does.
 * Everything else about the action is the card's: it still states the count, and it is still
 * inert until something is picked.
 */
export function AssetListSelectionBar({
	isSelecting,
	selectionCount,
	isDownloadPending,
	onStartSelecting,
	onExitSelecting,
	onDownloadSelected,
	isDisabled,
	size = 'sm',
	action,
}: AssetListSelectionBarProps) {
	const hasSelection = selectionCount > 0
	const ActionIcon = action?.icon ?? Download
	const control = cn(CONTROL, CONTROL_SIZE[size])

	if (!isSelecting) {
		// A host that named the way in owns its glyph too: a marquee cursor beside "Download as
		// a ZIP" states the gesture where the label already states the outcome.
		const EnterIcon = action?.enterLabel ? ActionIcon : SquareDashedMousePointer
		return (
			<button
				type="button"
				onClick={onStartSelecting}
				className={cn(control, 'border border-ds-border text-ds-text hover:bg-ds-surface')}
			>
				<EnterIcon aria-hidden className="size-4 text-ds-text-subtle" />
				{action?.enterLabel ?? 'Select assets'}
			</button>
		)
	}

	return (
		<div className="flex items-center gap-2">
			<Button
				size={size}
				isDisabled={!hasSelection || isDownloadPending || isDisabled}
				onPress={onDownloadSelected}
				className={control}
			>
				<ActionIcon aria-hidden className="size-4" />
				{isDownloadPending ? (action?.pendingLabel ?? 'Starting…') : (action?.label ?? downloadLabel)(selectionCount)}
			</Button>
			{/* Bordered like the way in, not a bare glyph: it is the same silhouette the reader
			    pressed to get here, and beside a filled action an unbounded icon reads as
			    decoration rather than as the control that leaves. */}
			<button
				type="button"
				onClick={onExitSelecting}
				aria-label="Done selecting"
				title="Done selecting"
				className={cn(
					control,
					EXIT_SIZE[size],
					'justify-center border border-ds-border px-0 text-ds-text-muted hover:bg-ds-surface hover:text-ds-text',
				)}
			>
				<X aria-hidden className="size-4" />
			</button>
		</div>
	)
}
